import { useEffect, useRef, useState } from 'react';

import { apiClient } from '../api/client.js';
import { useRealtimeInvalidation } from '../api/use-realtime.js';
import { useAuthStore } from '../auth/auth-store.js';

import { InboxPage } from './inbox-page.js';
import { SettingsChannels } from './settings/channels.js';
import { SettingsDataConnectors } from './settings/data-connectors.js';
import { WidgetsPage } from './widgets-page.js';

import '../layout/layout.css';
import './embed.css';

/**
 * ВСТРОЕННЫЙ РЕЖИМ — интерфейс внутри кабинета другого сервиса.
 *
 * Отличается от обычной админки двумя вещами, и обе намеренные:
 *
 *  1. Вход. Обычной страницы входа тут нет: сотрудник уже вошёл в кабинет,
 *     второй раз спрашивать пароль — ровно то, чего просили избежать.
 *     Кабинет кладёт одноразовый билет в адрес рамки, мы меняем его
 *     на сессию и держим её только в памяти вкладки.
 *
 *  2. Состав. Показываем то, ради чего сюда пришли: переписку, каналы,
 *     виджеты и источники данных. Аналитика, биллинг, роли, аудит остаются
 *     в полной версии — в узкой рамке чужого кабинета они не читаются,
 *     а дублировать управление аккаунтом в двух местах вредно.
 */

type Tab = 'inbox' | 'channels' | 'widgets' | 'data';

const TABS: Array<{ id: Tab; label: string; icon: string }> = [
  { id: 'inbox', label: 'Диалоги', icon: 'ti-messages' },
  { id: 'channels', label: 'Каналы', icon: 'ti-plug-connected' },
  { id: 'widgets', label: 'Чат на сайте', icon: 'ti-message-circle' },
  { id: 'data', label: 'Данные о покупках', icon: 'ti-building-store' },
];

interface ExchangeResponse {
  access_token: string;
  refresh_token: string;
  workspace: { id: string; slug: string; name: string };
  user: { id: string; email: string; name: string; role: string };
  target: {
    tab: Tab;
    ticket_id: string | null;
    customer_requested: boolean;
    /** Магазин, из которого открыт чат: сразу его вкладка */
    store_id?: string | null;
  } | null;
}

export function EmbedPage(): JSX.Element {
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const [state, setState] = useState<'exchanging' | 'ready' | 'failed'>('exchanging');
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab());
  const [target, setTarget] = useState<ExchangeResponse['target']>(null);
  const exchanged = useRef(false);

  useEffect(() => {
    // Строгий режим React вызывает эффекты дважды; билет одноразовый,
    // и второй обмен гарантированно провалится.
    if (exchanged.current) return;
    exchanged.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setError('Кабинет не передал билет для входа. Откройте раздел заново.');
      setState('failed');
      return;
    }

    void (async () => {
      try {
        const res = await fetch('/api/v1/partner/embed/exchange', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ embed_token: token }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as
            | { error?: { message?: string } }
            | null;
          throw new Error(body?.error?.message ?? `не удалось войти (${res.status})`);
        }
        const data = (await res.json()) as ExchangeResponse;

        apiClient.setEmbedSession(
          { access: data.access_token, refresh: data.refresh_token },
          () => {
            setError('Сессия истекла. Обновите страницу кабинета.');
            setState('failed');
          },
        );
        setUser(
          {
            id: data.user.id,
            workspaceId: data.workspace.id,
            workspaceSlug: data.workspace.slug,
            workspaceName: data.workspace.name,
            name: data.user.name,
            email: data.user.email,
            role: data.user.role,
          },
          // Не сохраняем: сессия кадра живёт только в памяти вкладки.
          // Запись сюда означала бы, что следующий обычный вход подхватит
          // сотрудника из чужого кабинета вместе со своим старым токеном.
          { persist: false },
        );

        // Куда открыть: из карточки заказа — сразу переписка с покупателем.
        if (data.target) {
          setTarget(data.target);
          setTab(data.target.tab);
        }
        // Билет отработал — убираем его из адреса, чтобы он не осел
        // в истории браузера и в Referer.
        window.history.replaceState({}, '', window.location.pathname);
        setState('ready');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'не удалось войти');
        setState('failed');
      }
    })();
  }, [setUser]);

  if (state !== 'ready') {
    return (
      <div className="inbx-embed__notice">
        {state === 'exchanging' ? 'Открываем чат…' : error}
      </div>
    );
  }

  return (
    <EmbedShell
      tab={tab}
      onTab={setTab}
      workspaceSlug={user?.workspaceSlug}
      target={target}
    />
  );
}

/**
 * Оболочка отделена от обмена билета намеренно: подписка на события реального
 * времени должна начинаться ПОСЛЕ появления сессии, иначе сокет уйдёт без
 * токена и оператор будет числиться не в сети.
 */
function EmbedShell(props: {
  tab: Tab;
  onTab: (t: Tab) => void;
  workspaceSlug: string | undefined;
  target: ExchangeResponse['target'];
}): JSX.Element {
  useRealtimeInvalidation(props.workspaceSlug);
  const noConversation = props.target?.customer_requested && !props.target.ticket_id;

  return (
    <div className="inbx-embed">
      <nav className="inbx-embed__tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`inbx-embed__tab ${props.tab === t.id ? 'is-active' : ''}`}
            onClick={() => props.onTab(t.id)}
          >
            <i className={`ti ${t.icon}`} />
            <span>{t.label}</span>
          </button>
        ))}
      </nav>
      {noConversation && props.tab === 'inbox' && (
        <div className="inbx-embed__notice inbx-embed__notice--inline">
          С этим покупателем переписки пока не было — он появится здесь, как только напишет.
        </div>
      )}
      <div className="inbx-embed__body">
        {props.tab === 'inbox' && <InboxPage
            initialTicketId={props.target?.ticket_id ?? null}
            initialStoreId={props.target?.store_id ?? null}
          />}
        {props.tab === 'channels' && <SettingsChannels />}
        {props.tab === 'widgets' && <WidgetsPage />}
        {props.tab === 'data' && <SettingsDataConnectors />}
      </div>
    </div>
  );
}

/** Кабинет может открыть сразу нужный раздел: /embed?tab=channels */
function initialTab(): Tab {
  const raw = new URLSearchParams(window.location.search).get('tab');
  return TABS.some((t) => t.id === raw) ? (raw as Tab) : 'inbox';
}
